import { validateDiscoveryResponse } from '../03-feed-session.mjs';
/** First visits wait only for authorized content. Persisted default-view updates
 * stay ordered through the canonical command queue and never drive navigation. */
export async function activateFeedSurface(
  ctx,
  {
    value,
    origin,
    actor,
    intent,
    selected,
    warmup,
    alignWarmup,
    paintFeedItems,
    setupPaging,
    saved,
  },
) {
  const destination = ctx.contentScope === 'learning' ? 'learning' : 'feed';
  const scope = JSON.stringify([
    ctx.mode,
    ctx.feedScope,
    ctx.contentScope ?? 'ordinary',
    ctx.discoveryPolicyVersion,
    ctx.state.settings?.learning,
    ctx.state.settings?.interestEpoch,
  ]);
  let committed = false,
    generation = ctx.feed.generation;
  const authorization = ctx.state;
  const active = () =>
    selected(intent) &&
    generation === ctx.feed.generation &&
    (committed || ctx.state === authorization) &&
    ctx.state?.me.id === actor &&
    ctx.route().join('/') === (committed ? destination : origin) &&
    scope ===
      JSON.stringify([
        ctx.mode,
        ctx.feedScope,
        ctx.contentScope ?? 'ordinary',
        ctx.discoveryPolicyVersion,
        ctx.state.settings?.learning,
        ctx.state.settings?.interestEpoch,
      ]);
  alignWarmup();
  const requestDone = ctx.api.timings?.start('first-screen-request', value);
  const renderDone = ctx.api.timings?.start('surface-switch-render', value, {
    targetRoute: `#/${destination}`,
  });
  const imageDone = ctx.api.timings?.start('first-image-ready', value, {
    targetRoute: `#/${destination}`,
  });
  try {
    let page;
    if (
      saved?.items.length &&
      saved.items.length <= 48 &&
      saved.meta?.discoveryPolicyVersion === ctx.discoveryPolicyVersion &&
      (saved.meta?.contentScope ?? 'ordinary') === (ctx.contentScope ?? 'ordinary')
    ) {
      const result = await ctx.api('/api/feed/hydrate', {
        ids: saved.items.map((item) => item.id),
        format: value,
        scope: ctx.feedScope,
        mode: ctx.mode,
        contentScope: ctx.contentScope ?? 'ordinary',
      });
      if (!active()) return;
      validateDiscoveryResponse(result, ctx.contentScope ?? 'ordinary', ctx.discoveryPolicyVersion);
      if (result.interestEpoch !== (ctx.state.settings?.interestEpoch ?? 0))
        throw new Error('Your feed preferences changed. Refresh to continue.');
      const omitted = new Set(result.missingIds ?? []);
      ctx.state.posts = ctx.state.posts.filter((post) => !omitted.has(post.id));
      const posts = new Map(result.posts.map((post) => [post.id, post]));
      for (const user of result.users ?? []) {
        const index = ctx.state.users?.findIndex((current) => current.id === user.id) ?? -1;
        if (index < 0) ctx.state.users?.push(user);
        else ctx.state.users[index] = user;
      }
      page = {
        ...saved.meta,
        format: value,
        scope: ctx.feedScope,
        mode: ctx.mode,
        contentScope: ctx.contentScope ?? 'ordinary',
        items: saved.items
          .filter((item) => posts.has(item.id))
          .map((item) => ({ ...item, post: posts.get(item.id) })),
        cursor: saved.cursor,
      };
    } else page = await warmup.activate(value);
    if (!page || !active()) return;
    requestDone?.();
    for (const user of page.users ?? []) {
      const index = ctx.state.users.findIndex((current) => current.id === user.id);
      if (index < 0) ctx.state.users.push(user);
      else ctx.state.users[index] = user;
    }
    for (const item of page.items) {
      const index = ctx.state.posts.findIndex((post) => post.id === item.id);
      if (index < 0) ctx.state.posts.push(item.post);
      else ctx.state.posts[index] = item.post;
    }
    ctx.feed.adopt(page, ctx.mode);
    generation = ctx.feed.generation;
    ctx.experience = value;
    ctx.feedScroll = saved?.scroll ?? 0;
    committed = true;
    ctx.go(destination);
    if (origin === destination) window.scrollTo({ top: ctx.feedScroll, behavior: 'instant' });
    ctx.api.timings?.afterPaint(renderDone ?? (() => {}));
    ctx.api.timings?.afterImage(
      imageDone ?? (() => {}),
      document.querySelector('#feed-list .post-photo, #feed-list .media-photo-button img'),
    );
    // Read acknowledgements and settings are secondary to the authorized target
    // paint. Their late completion cannot take over a newer route or selection.
    void ctx.flushSeen().catch((error) => {
      if (active()) ctx.toast(error.message);
    });
    void ctx
      .command({ type: 'settings', experience: value }, { paint: false })
      .then(async () => {
        if (!active() || ctx.experience !== value) return;
        paintFeedItems();
        if (ctx.feed.items.some((item) => !ctx.state.posts.some((post) => post.id === item.id))) {
          // Omitted rows are not permission to restore old DTOs. Only another
          // server-authorized page can repopulate this current surface.
          const refreshed = await warmup.activate(value);
          if (!refreshed || !active()) return;
          for (const item of refreshed.items) {
            const index = ctx.state.posts.findIndex((post) => post.id === item.id);
            if (index < 0) ctx.state.posts.push(item.post);
            else ctx.state.posts[index] = item.post;
          }
          ctx.feed.adopt(refreshed, ctx.mode);
          generation = ctx.feed.generation;
          paintFeedItems();
          setupPaging();
        }
      })
      .catch((error) => {
        if (active()) ctx.toast(error.message);
      });
  } catch (error) {
    if (active()) ctx.toast(error.message);
  }
}
